/**
 * Cobros: el aging de la cartera.
 *
 * Los tramos no son decorativos. La Ley 3/2004 fija 30 días de plazo
 * general y 60 como máximo pactable, así que un tramo «61-90» no es «un
 * poco tarde»: es deuda que ya devenga intereses de demora y los 40 € del
 * art. 8 **sin necesidad de reclamarlos**.
 */

import { TreasuryActions } from './treasury-actions.js';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSession } from '../session/session-context.js';
import { ApiError } from '../session/api-error.js';
import { money } from '../table/format.js';

interface MoneyJson {
  amount: string;
  currency: string;
}

interface AgingEntry {
  bucket: string;
  count: number;
  amount: MoneyJson;
}

interface AgingReport {
  invoiced: MoneyJson;
  collected: MoneyJson;
  outstanding: MoneyJson;
  overdue: MoneyJson;
  buckets: AgingEntry[];
  byCustomer: Array<{
    customerId: string;
    customerName: string;
    outstanding: MoneyJson;
    overdue: MoneyJson;
  }>;
}

const BUCKET_LABEL: Record<string, string> = {
  CURRENT: 'En plazo',
  D1_30: '1-30 días',
  D31_60: '31-60 días',
  D61_90: '61-90 días',
  D90_PLUS: 'Más de 90 días',
};

/** A partir de 31 días la deuda ya devenga intereses en el caso general. */
const SERIOUS = new Set(['D31_60', 'D61_90', 'D90_PLUS']);

export function CollectionsScreen(): React.ReactNode {
  const { api } = useSession();
  const [report, setReport] = useState<AgingReport | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    api
      .get<AgingReport>('/treasury/aging')
      .then((data) => {
        if (!cancelled) setReport(data);
      })
      .catch((caught: unknown) => {
        if (!cancelled) {
          setError(caught instanceof ApiError ? caught.message : 'No se ha podido cargar la cartera.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [api]);

  return (
    <section className="screen">
      <header className="screen__head">
        <h1 className="screen__title">Cobros</h1>
        <Link className="chip" to="/facturas?v=overdue">
          Ver facturas vencidas
        </Link>
      </header>

      {error !== null && (
        <p className="screen__error" role="alert">
          {error}
        </p>
      )}

      {loading && <p className="table__loading">Cargando la cartera…</p>}

      {report && (
        <>
          <div className="cards">
            <div className="card">
              <p className="card__label">Facturado</p>
              <p className="card__value">{money(report.invoiced.amount, report.invoiced.currency)}</p>
            </div>
            <div className="card">
              <p className="card__label">Cobrado</p>
              <p className="card__value">{money(report.collected.amount, report.collected.currency)}</p>
            </div>
            <div className="card">
              <p className="card__label">Pendiente</p>
              <p className="card__value">
                {money(report.outstanding.amount, report.outstanding.currency)}
              </p>
            </div>
            <div className="card card--alert">
              <p className="card__label">Vencido</p>
              <p className="card__value">{money(report.overdue.amount, report.overdue.currency)}</p>
            </div>
          </div>

          <h2 className="screen__subtitle">Por antigüedad</h2>
          <div className="scroller">
            <table className="table__grid table__grid--compact">
              <thead>
                <tr>
                  <th>Tramo</th>
                  <th className="table__num">Facturas</th>
                  <th className="table__num">Importe</th>
                </tr>
              </thead>
              <tbody>
                {report.buckets.map((entry) => (
                  <tr key={entry.bucket}>
                    <td>
                      {SERIOUS.has(entry.bucket) ? (
                        <span className="due due--late">⚠ {BUCKET_LABEL[entry.bucket] ?? entry.bucket}</span>
                      ) : (
                        (BUCKET_LABEL[entry.bucket] ?? entry.bucket)
                      )}
                    </td>
                    <td className="table__num">{entry.count}</td>
                    <td className="table__num">{money(entry.amount.amount, entry.amount.currency)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="screen__note">
            A partir de 31 días la deuda devenga interés de demora y los 40 € de compensación del
            art. 8 de la Ley 3/2004, <strong>sin necesidad de reclamarlos</strong>. La ficha de cada
            factura calcula el importe exacto.
          </p>

          <h2 className="screen__subtitle">Por cliente</h2>
          <div className="scroller">
            <table className="table__grid table__grid--compact">
              <thead>
                <tr>
                  <th>Cliente</th>
                  <th className="table__num">Pendiente</th>
                  <th className="table__num">Vencido</th>
                </tr>
              </thead>
              <tbody>
                {report.byCustomer.map((row) => (
                  <tr key={row.customerId}>
                    <td>{row.customerName}</td>
                    <td className="table__num">
                      {money(row.outstanding.amount, row.outstanding.currency)}
                    </td>
                    <td className="table__num">
                      {Number(row.overdue.amount) > 0 ? (
                        <span className="due due--late">
                          {money(row.overdue.amount, row.overdue.currency)}
                        </span>
                      ) : (
                        money(row.overdue.amount, row.overdue.currency)
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    
      <TreasuryActions />
    </section>
  );
}
